import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { createSetupIntent } from './api/accountApi';
import { accountModalAuthButtonClass } from './accountStyles';
import { useAccountGetToken } from './hooks/useAccountApi';
import { getStripeBrowser } from './stripeBrowser';

type Profile = { email?: string; name?: string };

function AddCardForm({
  onCancel,
  onSaved
}: {
  onCancel: () => void;
  onSaved: (paymentMethodId: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErrorMessage(null);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}${window.location.pathname}${window.location.search}`
      }
    });
    setBusy(false);
    if (error) {
      setErrorMessage(error.message ?? 'Card setup failed');
      return;
    }
    const pm = setupIntent?.payment_method;
    const pmId = typeof pm === 'string' ? pm : pm?.id;
    if (!pmId) {
      setErrorMessage('Could not read payment method');
      return;
    }
    onSaved(pmId);
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <PaymentElement />
      {errorMessage ? (
        <p className="text-sm text-red-600" role="alert">
          {errorMessage}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-2">
        <button
          type="submit"
          disabled={!stripe || busy}
          className={`flex-1 min-w-[120px] ${accountModalAuthButtonClass} disabled:opacity-50`}
        >
          {busy ? 'Saving…' : 'Save card'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export interface AddCardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile?: Profile;
  onCardSaved?: (paymentMethodId: string) => void;
}

export function AddCardModal({
  open,
  onOpenChange,
  profile,
  onCardSaved
}: AddCardModalProps) {
  const getToken = useAccountGetToken();
  const qc = useQueryClient();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  const stripePromise = useMemo(() => getStripeBrowser(), []);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setInitError(null);
      return;
    }

    let cancelled = false;
    setLoadingIntent(true);
    setInitError(null);
    void (async () => {
      try {
        const { clientSecret: secret } = await createSetupIntent(getToken, {
          email: profile?.email,
          name: profile?.name
        });
        if (!cancelled) setClientSecret(secret);
      } catch (e) {
        if (!cancelled) {
          setInitError(
            e instanceof Error ? e.message : 'Could not load card form'
          );
        }
      } finally {
        if (!cancelled) setLoadingIntent(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, getToken, profile?.email, profile?.name]);

  useEffect(() => {
    if (!open) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  function handleSaved(paymentMethodId: string) {
    void qc.invalidateQueries({ queryKey: ['account', 'paymentMethods'] });
    onCardSaved?.(paymentMethodId);
    onOpenChange(false);
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[1px]"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-card-title"
        className="relative z-[101] w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/15 ring-1 ring-white/80"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2
              id="add-card-title"
              className="text-lg font-semibold tracking-tight text-slate-900"
            >
              Add card
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Secure card entry powered by Stripe. Your card is saved for wallet
              top-ups.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className="mt-6">
          {loadingIntent ? (
            <p className="text-sm text-slate-600">Loading secure form…</p>
          ) : initError ? (
            <p className="text-sm text-red-600" role="alert">
              {initError}
            </p>
          ) : clientSecret ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#2b6cff',
                    borderRadius: '8px'
                  }
                }
              }}
            >
              <AddCardForm
                onCancel={() => onOpenChange(false)}
                onSaved={handleSaved}
              />
            </Elements>
          ) : (
            <p className="text-sm text-slate-600">Unable to start setup.</p>
          )}
        </div>
      </div>
    </div>
  );
}
